import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCallback, useEffect, useState } from "react";

export interface ChatThread {
  id: string;
  title: string;
  updated_at: string;
}

export function useChatThreads() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setThreads([]);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("chat_threads" as any)
      .select("id,title,updated_at")
      .eq("user_id", user.id)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false })
      .limit(50);
    setThreads((data as any) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (title = "New chat"): Promise<string | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from("chat_threads" as any)
      .insert({ user_id: user.id, title })
      .select("id")
      .single();
    if (error) return null;
    await refresh();
    return (data as any).id as string;
  }, [user, refresh]);

  const remove = useCallback(async (id: string) => {
    if (!user) return;
    await supabase
      .from("chat_threads" as any)
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", user.id);
    await refresh();
  }, [user, refresh]);

  const rename = useCallback(async (id: string, title: string) => {
    if (!user) return;
    await supabase
      .from("chat_threads" as any)
      .update({ title })
      .eq("id", id)
      .eq("user_id", user.id);
    await refresh();
  }, [user, refresh]);

  return { threads, loading, refresh, create, remove, rename };
}

export async function loadThreadMessages(threadId: string) {
  const { data } = await (supabase as any)
    .from("chat_messages")
    .select("id,role,content,parts,created_at")
    .eq("thread_id", threadId)
    .order("created_at", { ascending: true });
  return (data ?? []).map((row: any) => ({
    id: row.id,
    role: row.role,
    parts: row.parts ?? [{ type: "text", text: row.content ?? "" }],
  }));
}
